import {
  IProductPricedPerCollectionLevel,
  IProductsPricedPerCollectionCollection,
} from '@engyalo/storefront-types/lib/graphql/product'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { ICartDictionary, IPreloadedCart, IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { get, set } from 'lodash'
import { generateCartId } from '../lib/cartIdGenerator'
import { getTotalPrice } from '../lib/getTotalPrice'

export const getPreloadedCartFromSession = (orderConfig: IOrderConfig | null) => {
  try {
    const sessionMetadata = JSON.parse(orderConfig?.metadata || '{}')
    if (sessionMetadata.cart) {
      return sessionMetadata.cart as IPreloadedCart[]
    }
  } catch {
    // TODO: Warn sentry that a preloaded cart exist but it was bad formatted
  }
  return null
}

export const getProductFromPreloadedCart = (
  collections: (IProductsPricedPerCollectionCollection | null)[],
  cartId: string
): IProduct | null => {
  for (const collection of collections) {
    const product = collection?.productsPriced?.find((product: IProduct | null) => {
      const [productId] = cartId.split('_')
      return product && generateCartId(product).includes(productId)
    })

    if (product) {
      return product
    }
  }
  return null
}

export const getCartFromPreloadedCartItem = (
  cart: ICartDictionary,
  product: IProduct,
  quantity: number,
  shouldReplace = false,
  cartId: string | null = null
): ICartDictionary => {
  const tempCart = { ...cart }
  if (product && product.id) {
    const currentSku = cartId || generateCartId(product)
    if (tempCart[currentSku]) {
      const tempProd = { ...tempCart[currentSku] }
      if (tempProd.quantity) {
        const totalPrice = getTotalPrice(product)
        tempProd.quantity = shouldReplace ? quantity : tempProd.quantity + quantity
        if (tempProd.quantity > 0) {
          tempCart[currentSku] = {
            ...tempProd,
            totalPrice,
          }
        } else {
          delete tempCart[currentSku]
        }
      }
    } else if (quantity > 0) {
      const totalPrice = getTotalPrice(product)
      tempCart[currentSku] = {
        ...product,
        totalPrice,
        quantity,
        cartId: currentSku,
      } as IProduct
    }
  }
  return tempCart
}

export const getPreloadedCartWithCustomizations = (
  oldCart: ICartDictionary,
  collections: IProductsPricedPerCollectionCollection[],
  preloadedCart: IPreloadedCart[]
) => {
  const newCart = { ...oldCart }
  const products = collections.flatMap((collection) => collection?.productsPriced)
  preloadedCart?.forEach(({ cartId }) => {
    const [productId, ...customizations] = cartId.split('_')

    if (!customizations) {
      return
    }

    const productCustomizations = {
      customizations: [] as IProductPricedPerCollectionLevel[],
    } as IProductPricedPerCollectionLevel
    customizations.forEach((customization, customizationIndex) => {
      let product: IProductPricedPerCollectionLevel | null =
        (products?.find((product) => product?.id === productId) as IProductPricedPerCollectionLevel) || null
      const customizationProducts = customization
        .split('-')
        .map((productId, index) => ({ id: productId, nested: index !== 0 }))
      customizationProducts.forEach(({ id: customizationId, nested }, index) => {
        const customizationProduct =
          product?.customizations?.find((customization) => customization?.id === customizationId) || null
        if (!nested) {
          product = customizationProduct
        }

        if (!product) {
          return
        }

        const {
          customizations,
          ...productWithoutCustomizations
        } = customizationProduct as IProductPricedPerCollectionLevel
        const prop = Array.from({ length: Math.min(index + 1, 2) }).reduce(
          (prev, _, idx) => `${prev}${idx > 0 ? `[${customizationIndex}].` : ''}customizations`,
          ''
        ) as string
        set(productCustomizations, prop, [...get(productCustomizations, prop, []), productWithoutCustomizations])
      })
    })
    newCart[cartId] = {
      ...newCart[cartId],
      customizations: productCustomizations.customizations,
    }
  })

  return newCart
}

export const generatePreloadedCart = (cart: ICartDictionary) => {
  const items = Object.values(cart).map((product) => {
    return {
      quantity: product.quantity,
      cartId: product.cartId,
      unitType: product.sizeDescription,
    }
  })
  return JSON.stringify({ cart: items })
}
